import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PartyPopper, Zap, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEADPHONE_IMG =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_urban-rides-26/artifacts/zelu2r85_f9dfc855-2630-4d22-abbb-adf04e68ed35.png";
const JBL_IMG =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_urban-rides-26/artifacts/emhgxxoo_JBL_XTREME_4_HERO_BLACK_47136_X2_%281%29%20%281%29.webp";

// 12 fatias de 30° — 30/50/100, o FONE e a JBL são apenas visuais; resultados reais: 5%, 10% e 20% (raro)
const SEGMENTS = [
  { label: "5%", value: 5, color: "#FFCC00", text: "#000" },
  { label: "30%", value: 30, color: "#FF3B30", text: "#fff" },
  { label: "10%", value: 10, color: "#2A2A31", text: "#FFCC00" },
  { label: "JBL", value: "jbl", color: "#FFCC00", img: JBL_IMG },
  { label: "50%", value: 50, color: "#FF3B30", text: "#fff" },
  { label: "5%", value: 5, color: "#2A2A31", text: "#FFCC00" },
  { label: "FONE", value: "fone", color: "#FFCC00", img: HEADPHONE_IMG },
  { label: "10%", value: 10, color: "#FF3B30", text: "#fff" },
  { label: "100%", value: 100, color: "#2A2A31", text: "#FFCC00" },
  { label: "5%", value: 5, color: "#FFCC00", text: "#000" },
  { label: "20%", value: 20, color: "#FF3B30", text: "#fff" },
  { label: "10%", value: 10, color: "#2A2A31", text: "#FFCC00" },
];
const SEG_ANGLE = 360 / SEGMENTS.length;

const WHEEL_BG = `conic-gradient(${SEGMENTS.map(
  (s, i) => `${s.color} ${i * SEG_ANGLE}deg ${(i + 1) * SEG_ANGLE}deg`
).join(", ")})`;

// ---- Sons gerados via Web Audio (sem arquivos externos) ----
let audioCtx = null;
const getCtx = () => {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    return audioCtx;
  } catch {
    return null;
  }
};

const playTicks = (durationMs) => {
  const ctx = getCtx();
  if (!ctx) return;
  const start = ctx.currentTime;
  const total = durationMs / 1000;

  // Camada 1: "whoosh" de energia subindo durante o giro
  const whoosh = ctx.createOscillator();
  const whooshGain = ctx.createGain();
  whoosh.type = "sawtooth";
  whoosh.frequency.setValueAtTime(90, start);
  whoosh.frequency.exponentialRampToValueAtTime(420, start + total);
  whooshGain.gain.setValueAtTime(0.0001, start);
  whooshGain.gain.exponentialRampToValueAtTime(0.045, start + 0.4);
  whooshGain.gain.setValueAtTime(0.045, start + total - 0.8);
  whooshGain.gain.exponentialRampToValueAtTime(0.0001, start + total);
  whoosh.connect(whooshGain).connect(ctx.destination);
  whoosh.start(start);
  whoosh.stop(start + total + 0.1);

  // Camada 2: melodia animada estilo cassino (notas saltitantes em loop)
  const melody = [523.25, 659.25, 783.99, 659.25, 880.0, 783.99, 1046.5, 783.99];
  let mt = 0;
  let step = 0;
  while (mt < total - 0.6) {
    const f = melody[step % melody.length];
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = f;
    const t0 = start + mt;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.11, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.16);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.2);
    step += 1;
    // acelera no começo, desacelera no fim (suspense)
    const progress = mt / total;
    mt += 0.16 + progress * 0.16;
  }

  // Camada 3: ticks percussivos que desaceleram (suspense no fim)
  let t = 0;
  let gap = 0.03;
  while (t < total) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 2200 - (t / total) * 1000;
    gain.gain.setValueAtTime(0.07, start + t);
    gain.gain.exponentialRampToValueAtTime(0.001, start + t + 0.03);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start + t);
    osc.stop(start + t + 0.035);
    t += gap;
    gap = Math.min(gap * 1.06, 0.5);
  }
};

const playWin = () => {
  const ctx = getCtx();
  if (!ctx) return;
  // Fanfarra de vitória animada e brilhante
  const notes = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98, 2093.0];
  notes.forEach((f, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = f;
    const t0 = ctx.currentTime + i * 0.1;
    gain.gain.setValueAtTime(0.19, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.5);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.55);
    // Harmônico brilhante
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.value = f * 2;
    gain2.gain.setValueAtTime(0.07, t0);
    gain2.gain.exponentialRampToValueAtTime(0.001, t0 + 0.4);
    osc2.connect(gain2).connect(ctx.destination);
    osc2.start(t0);
    osc2.stop(t0 + 0.45);
  });
  // Acorde final de celebração
  const chord = [523.25, 659.25, 783.99, 1046.5];
  const tc = ctx.currentTime + notes.length * 0.1 + 0.05;
  chord.forEach((f) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = f;
    gain.gain.setValueAtTime(0.13, tc);
    gain.gain.exponentialRampToValueAtTime(0.001, tc + 1.1);
    osc.connect(gain).connect(ctx.destination);
    osc.start(tc);
    osc.stop(tc + 1.2);
  });
};

const CONFETTI_COLORS = ["#FFCC00", "#FF3B30", "#25D366", "#FFFFFF"];

// Avalia a curva cubic-bezier (mesma do giro) para sincronizar o ponteiro com as bordas das fatias
const cubicBezier = (p1x, p1y, p2x, p2y) => {
  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t) => ((ay * t + by) * t + cy) * t;
  const sampleDX = (t) => (3 * ax * t + 2 * bx) * t + cx;
  const solveX = (x) => {
    let t = x;
    for (let i = 0; i < 8; i++) {
      const err = sampleX(t) - x;
      if (Math.abs(err) < 1e-4) return t;
      const d = sampleDX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    return t;
  };
  return (x) => sampleY(solveX(x));
};
const EASE = cubicBezier(0.1, 0.72, 0.1, 1);


export default function Roulette({ phone, name }) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const pointerRef = useRef(null);

  const flickPointer = () => {
    const el = pointerRef.current;
    if (!el) return;
    el.animate(
      [
        { transform: "rotate(0deg)" },
        { transform: "rotate(-16deg)" },
        { transform: "rotate(0deg)" },
      ],
      { duration: 160, easing: "cubic-bezier(.3,.7,.4,1)" }
    );
  };

  const spin = async () => {
    if (spinning || result !== null) return;
    setSpinning(true);
    try {
      const { data } = await axios.post(`${API}/roulette/spin`, { phone, name });
      if (data.already_played) {
        setAlreadyPlayed(true);
        setExpiresAt(data.expires_at);
        setResult(data.discount);
        setSpinning(false);
        return;
      }
      const idxs = SEGMENTS.map((s, i) => (s.value === data.discount ? i : -1)).filter((i) => i >= 0);
      const idx = idxs[Math.floor(Math.random() * idxs.length)];
      const segCenter = idx * SEG_ANGLE + SEG_ANGLE / 2;
      const jitter = Math.random() * (SEG_ANGLE * 0.5) - SEG_ANGLE * 0.25;
      const startRot = rotation;
      const base = Math.ceil(startRot / 360) * 360;
      const target = base + 8 * 360 + (360 - segCenter) + jitter;
      setRotation(target);
      playTicks(7500);
      if (navigator.vibrate) navigator.vibrate(80);

      // Ponteiro bate SOMENTE ao cruzar a borda entre uma fatia e outra
      const duration = 7500;
      const startTime = performance.now();
      let lastBoundary = Math.floor(startRot / SEG_ANGLE);
      const loop = (now) => {
        const p = Math.min((now - startTime) / duration, 1);
        const cur = startRot + (target - startRot) * EASE(p);
        const boundary = Math.floor(cur / SEG_ANGLE);
        if (boundary !== lastBoundary) {
          lastBoundary = boundary;
          flickPointer();
        }
        if (p < 1) requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);

      setTimeout(() => {
        setExpiresAt(data.expires_at);
        setResult(data.discount);
        setSpinning(false);
        setCelebrate(true);
        playWin();
        if (navigator.vibrate) navigator.vibrate([120, 60, 120, 60, 240]);
        setTimeout(() => setCelebrate(false), 4500);
      }, 7500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao girar a roleta. Tente novamente.");
      setSpinning(false);
    }
  };

  const expired = expiresAt ? new Date(expiresAt) < new Date() : false;

  return (
    <div className="relative flex flex-col items-center" data-testid="roulette">
      {celebrate ? (
        <div className="pointer-events-none absolute inset-0 overflow-visible z-20" data-testid="roulette-confetti">
          {Array.from({ length: 28 }).map((_, i) => (
            <span
              key={i}
              className="confetti-piece"
              style={{
                left: `${(i * 37) % 100}%`,
                backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                animationDelay: `${(i % 7) * 0.12}s`,
                animationDuration: `${2 + (i % 5) * 0.4}s`,
                transform: `rotate(${(i * 53) % 360}deg)`,
              }}
            />
          ))}
        </div>
      ) : null}
      {/* Ponteiro */}
      <div
        className="relative"
        style={{
          transform: spinning ? "scale(1.3)" : "scale(1)",
          transition: "transform 0.7s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <div className="absolute left-1/2 -translate-x-1/2 -top-5 z-30 pointer-events-none">
          <svg
            ref={pointerRef}
            width="48"
            height="58"
            viewBox="0 0 48 58"
            fill="none"
            className="drop-shadow-[0_4px_12px_rgba(0,0,0,0.65)]"
            style={{ transformOrigin: "50% 32%" }}
          >
            <defs>
              <linearGradient id="troyPointer" x1="24" y1="2" x2="24" y2="56" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFF0B0" />
                <stop offset="0.45" stopColor="#FFCC00" />
                <stop offset="1" stopColor="#FF3B30" />
              </linearGradient>
              <radialGradient id="troyPointerGloss" cx="0.36" cy="0.28" r="0.75">
                <stop stopColor="#FFFFFF" stopOpacity="0.85" />
                <stop offset="0.45" stopColor="#FFFFFF" stopOpacity="0" />
              </radialGradient>
            </defs>
            <path
              d="M24 55 L8 22 A16 16 0 1 1 40 22 Z"
              fill="url(#troyPointer)"
              stroke="#FFFFFF"
              strokeWidth="2.5"
              strokeLinejoin="round"
            />
            <path d="M24 55 L8 22 A16 16 0 1 1 40 22 Z" fill="url(#troyPointerGloss)" />
            <circle cx="24" cy="19" r="6.5" fill="#08080A" stroke="#FFCC00" strokeWidth="2" />
            <circle cx="24" cy="19" r="2.4" fill="#FFCC00" />
          </svg>
        </div>
        <div
          className="relative p-2.5 rounded-full bg-[#08080A] border-4 border-[#FFCC00]/40 shadow-[0_0_55px_rgba(255,204,0,0.3)]"
          style={{
            animation: spinning ? "pulseGlowYellow 0.9s ease-in-out infinite" : "none",
          }}
        >
          {/* Luzes de cassino ao redor da roleta */}
          <div className="pointer-events-none absolute inset-0 z-20 overflow-visible">
            {Array.from({ length: 20 }).map((_, i) => (
              <span
                key={i}
                className="roulette-light absolute left-1/2 top-1/2 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
                style={{
                  transform: `translate(-50%, -50%) rotate(${i * 18}deg) translateY(-166px)`,
                  animation: `${
                    spinning ? "rouletteLightChase 0.3s" : "rouletteLightBlink 1.3s"
                  } ease-in-out ${(i % 4) * 0.08}s infinite`,
                }}
              />
            ))}
          </div>
          <div
            data-testid="roulette-wheel"
            className="relative z-10 w-72 h-72 sm:w-80 sm:h-80 rounded-full border-4 border-black/60"
            style={{
              background: WHEEL_BG,
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? "transform 7.5s cubic-bezier(0.1, 0.72, 0.1, 1)" : "none",
            }}
          >
            {SEGMENTS.map((s, i) =>
              s.img ? (
                <div
                  key={i}
                  className="absolute left-1/2 top-1/2 flex items-center justify-center rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.5)] ring-2 ring-[#FFCC00] w-14 h-14 sm:w-16 sm:h-16 select-none pointer-events-none"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${
                      i * SEG_ANGLE + SEG_ANGLE / 2
                    }deg) translateY(-112px)`,
                  }}
                >
                  <img
                    src={s.img}
                    alt={s.label === "JBL" ? "Caixa de som JBL" : "Fone TROY wireless"}
                    className="w-11 h-11 sm:w-12 sm:h-12 object-contain"
                  />
                </div>
              ) : (
                <span
                  key={i}
                  className="absolute left-1/2 top-1/2 font-display font-black text-sm sm:text-base select-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]"
                  style={{
                    color: s.text,
                    transform: `translate(-50%, -50%) rotate(${
                      i * SEG_ANGLE + SEG_ANGLE / 2
                    }deg) translateY(-116px)`,
                  }}
                >
                  {s.label}
                </span>
              )
            )}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#08080A] border-2 border-[#FFCC00] flex items-center justify-center shadow-[0_0_20px_rgba(255,204,0,0.4)]">
              <Zap className="w-6 h-6 text-[#FFCC00]" />
            </div>
          </div>
        </div>
      </div>

      {result === null ? (
        <button
          type="button"
          onClick={spin}
          disabled={spinning}
          data-testid="roulette-spin-btn"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white font-black text-lg py-4 px-12 rounded-xl hover:bg-[#DC2626] transition-all transform hover:-translate-y-0.5 shadow-[0_0_35px_rgba(255,59,48,0.45)] disabled:opacity-70 animate-[pulseGlowYellow_2.4s_ease-in-out_infinite]"
        >
          {spinning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
          {spinning ? "GIRANDO..." : "🎰 GIRAR A ROLETA"}
        </button>
      ) : (
        <div className="mt-6 w-full max-w-sm" data-testid="roulette-result">
          <div className="text-center mb-4 animate-[popIn_0.5s_cubic-bezier(0.34,1.56,0.64,1)]">
            <PartyPopper className="w-9 h-9 text-[#FFCC00] mx-auto mb-2" />
            <p className="font-display text-xl sm:text-2xl font-black text-white">
              {alreadyPlayed ? "Este número já participou!" : "🎉 Parabéns! Você ganhou:"}
            </p>
          </div>

          {/* Cupom para print */}
          <div
            data-testid="roulette-voucher"
            className="card-accent-top rounded-2xl bg-[#0D0D10] border-2 border-dashed border-[#FFCC00]/50 p-6 text-center"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold mb-1">
              Troy Transportes
            </p>
            <p className="font-display text-lg font-black text-white mb-3">
              🎟️ CUPOM DE DESCONTO
            </p>
            <p
              data-testid="voucher-discount"
              className="font-display text-5xl font-black text-[#FFCC00] drop-shadow-[0_0_18px_rgba(255,204,0,0.45)] mb-4"
            >
              {result}% OFF
            </p>
            <div className="text-left text-sm space-y-1.5 bg-white/5 rounded-xl p-4 border border-white/10">
              <p data-testid="voucher-name">
                <span className="text-white/40">Nome:</span>{" "}
                <strong className="text-white">{name || "—"}</strong>
              </p>
              <p data-testid="voucher-phone">
                <span className="text-white/40">Telefone:</span>{" "}
                <strong className="text-white">{phone}</strong>
              </p>
              <p data-testid="voucher-date">
                <span className="text-white/40">Data:</span>{" "}
                <strong className="text-white">
                  {new Date().toLocaleDateString("pt-BR")}
                </strong>
              </p>
              <p data-testid="voucher-expiry">
                <span className="text-white/40">Válido até:</span>{" "}
                <strong className={expired ? "text-[#FF3B30]" : "text-[#FFCC00]"}>
                  {expiresAt ? new Date(expiresAt).toLocaleDateString("pt-BR") : "7 dias após o giro"}
                  {expired ? " (EXPIRADO)" : ""}
                </strong>
              </p>
            </div>
          </div>

          <div
            data-testid="roulette-info"
            className="mt-4 rounded-2xl bg-[#FFCC00]/10 border border-[#FFCC00]/30 p-4"
          >
            <p className="flex items-center justify-center gap-2 font-display font-black text-[#FFCC00] text-sm mb-2">
              <Lock className="w-4 h-4" />
              CUPOM PESSOAL E INTRANSFERÍVEL
            </p>
            <p className="text-white/70 text-xs leading-relaxed text-center">
              Este cupom é válido <strong className="text-white">somente para o seu número</strong>{" "}
              (<span className="text-[#FFCC00] font-bold">{phone}</span>) e{" "}
              <strong className="text-white">não pode ser compartilhado</strong>. Ao pedir sua
              corrida no grupo da Troy, informe o mesmo telefone para garantir o seu desconto.
            </p>
          </div>

          <p className="text-[#FFCC00] font-bold text-sm mt-4 text-center">
            📸 Tire um PRINT desta tela para validar seu desconto!
          </p>
          <p className="text-white/50 text-xs mt-1 text-center max-w-xs mx-auto">
            Apresente o print ao pedir sua corrida no grupo da Troy. Válido uma
            única vez por número e por 7 dias a partir do giro.
          </p>
        </div>
      )}
    </div>
  );
}
